import { ApiRequest, fetchDataFromAPITyped } from "./api-requests";
import { SupportedChargerAttributes, SupportedChargerQuery } from "../models/supported-charger-model";

/**
 * Response types for Supported Chargers API
 */

// GET /api/supported-chargers/
export type GetSupportedChargersResponse_t = {
  success: boolean;
  count: number;
  data: SupportedChargerAttributes[];
}

// GET /api/supported-chargers/manufacturers
export type GetManufacturersResponse_t = {
  success: boolean;
  count: number;
  data: string[];
}

// GET /api/supported-chargers/by-manufacturer
export type GetChargersByManufacturerResponse_t = {
  success: boolean;
  data: Record<string, SupportedChargerAttributes[]>;
}

// GET /api/supported-chargers/:id
export type GetSupportedChargerByIdResponse_t = {
  success: boolean;
  data: SupportedChargerAttributes;
}

// GET /api/supported-chargers/specs
export type GetChargerSpecsResponse_t = {
  success: boolean;
  count: number;
  data: SupportedChargerAttributes[];
}

// POST /api/supported-chargers/validate
export interface ValidateChargerRequest {
  manufacturer: string;
  model?: string;
  sku?: string;
}

export type ValidateChargerResponse_t = {
  success: boolean;
  supported: boolean;
  message: string;
}

/**
 * Service methods for Supported Chargers
 */

/**
 * Get all supported chargers with optional filtering
 * @param query - Optional filters for manufacturer, family, model, SKU, region, and OCPP support
 * @returns Array of supported chargers
 */
export const getSupportedChargers = async (query?: SupportedChargerQuery) => {
  // Build query string from query parameters
  const queryParams = new URLSearchParams();
  if (query) {
    if (query.manufacturer) queryParams.append('manufacturer', query.manufacturer);
    if (query.family) queryParams.append('family', query.family);
    if (query.model) queryParams.append('model', query.model);
    if (query.sku) queryParams.append('sku', query.sku);
    if (query.region) queryParams.append('region', query.region);
    if (query.ocppSupported !== undefined) {
      queryParams.append('ocppSupported', query.ocppSupported.toString());
    }
  }
  
  const queryString = queryParams.toString();
  const route = queryString ? `supported-chargers?${queryString}` : 'supported-chargers';
  
  const apiQuery: ApiRequest = {
    route,
    request: {
      method: 'GET',
      entity: 'supported-chargers',
      body: '',
    }
  };
  return await fetchDataFromAPITyped<GetSupportedChargersResponse_t>(apiQuery);
}

/**
 * Get all unique manufacturers
 * @returns Array of unique manufacturer names
 */
export const getManufacturers = async () => {
  const apiQuery: ApiRequest = {
    route: "supported-chargers/manufacturers",
    request: {
      method: 'GET',
      entity: 'supported-chargers',
      body: '',
    }
  };
  return await fetchDataFromAPITyped<GetManufacturersResponse_t>(apiQuery);
}

/**
 * Get supported chargers grouped by manufacturer
 * @returns Object with manufacturers as keys and their supported chargers as values
 */
export const getSupportedChargersByManufacturer = async () => {
  const apiQuery: ApiRequest = {
    route: "supported-chargers/by-manufacturer",
    request: {
      method: 'GET',
      entity: 'supported-chargers',
      body: '',
    }
  };
  return await fetchDataFromAPITyped<GetChargersByManufacturerResponse_t>(apiQuery);
}

/**
 * Get a specific supported charger by ID
 * @param id - Supported charger ID
 * @returns Supported charger info or error
 */
export const getSupportedChargerById = async (id: number) => {
  const apiQuery: ApiRequest = {
    route: `supported-chargers/${id}`,
    request: {
      method: 'GET',
      entity: 'supported-chargers',
      body: '',
    }
  };
  return await fetchDataFromAPITyped<GetSupportedChargerByIdResponse_t>(apiQuery);
}

/**
 * Get charger specifications by manufacturer and optional model
 * Useful for displaying charger details during registration
 * @param manufacturer - Manufacturer name (required)
 * @param model - Optional model name
 * @returns Array of matching chargers with full specs
 */
export const getChargerSpecs = async (manufacturer: string, model?: string) => {
  const queryParams = new URLSearchParams();
  queryParams.append('manufacturer', manufacturer);
  if (model) {
    queryParams.append('model', model);
  }
  
  const apiQuery: ApiRequest = {
    route: `supported-chargers/specs?${queryParams.toString()}`,
    request: {
      method: 'GET',
      entity: 'supported-chargers',
      body: '',
    }
  };
  return await fetchDataFromAPITyped<GetChargerSpecsResponse_t>(apiQuery);
}

/**
 * Validate if a charger is supported
 * @param request - Object containing manufacturer (required), model (optional), and sku (optional)
 * @returns Object indicating if charger is supported
 */
export const validateChargerSupport = async (request: ValidateChargerRequest) => {
  const apiQuery: ApiRequest = {
    route: "supported-chargers/validate",
    request: {
      method: 'POST',
      entity: 'supported-chargers',
      body: request,
    }
  };
  return await fetchDataFromAPITyped<ValidateChargerResponse_t>(apiQuery);
}

